import React, { useContext, useEffect, useMemo, useState } from "react";
import "./Fisheries.scss";
import { MapContext } from "../../../context/MapContext";
import GeoSelector from "../../GeoSelector/GeoSelector";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import QueryTask from "@arcgis/core/tasks/QueryTask";
import Query from "@arcgis/core/tasks/support/Query";
import GeoScale from "../../GeoScale/GeoScale";
import FisheriesStats from "./FisheriesStats/FisheriesStats";
import FisheriesConfig from "./FisheriesConfig";
import useQueryString from "../../../hooks/useQueryString";
//import ReferenceLayers from "../../ReferenceLayers/ReferenceLayers";
import FisheriesChart from "./FisheriesChart/FisheriesChart";
import geometries from "../../../constants/geometries";
import Extent from "@arcgis/core/geometry/Extent";

import Form from "calcite-react/Form/Form";
import utils from "../../../helpers/utils";
import References from "../../References/References";
import ReferenceLayers from "../../ReferenceLayers/ReferenceLayers";
import { LayoutContext } from "../../../context/LayoutContext";
import { GeoContext } from "../../../context/GeoContext";

function Fisheries() {
  const [mapObject] = useContext(MapContext);
  const { mobileActive } = useContext(LayoutContext);
  const {setSelections, setSelectedGeometry, selectedGeometry} = useContext(GeoContext);

  const [isGeoScaled, setIsGeoScaled] = useQueryString("isGeoScaled", false);
  const [selectedStat, setSelectedStat] = useQueryString(
    "selectedStat",
    "Estimated cumulative fishing impact"
  );
  const [stats, setStats] = useState([]);

  const [contextLayers, setContextLayers] = useState([]);
  const dropdownCountries = ["Eastern Caribbean", ...FisheriesConfig.countries];
  const [refLayersEnabled, setRefLayersEnabled] = useQueryString(
    "refLayers",
    []
  );

  useEffect(() => {
    setSelections(dropdownCountries);
    setSelectedGeometry(dropdownCountries[0]);
  }, []);

  /**
   * Every time this route is accessed, make sure the EEZ layer reflects the correct boundaries
   */
  useEffect(() => {
    const eez = mapObject?.map.findLayerById("eez");
    if (eez) {
      eez.definitionExpression = `Dissolve in (${FisheriesConfig.countries
        .map((c) => "'" + utils.getCountryForEEZ(c) + "'")
        .join(",")})`;
    }
  }, [mapObject]);

  /**
   * Function that looks up layer object from config file to use based on current
   * app selections (selected geometry)
   */
  const getLayersForSelections = (stat, geometry, isScaled) => {
    const dataSet = FisheriesConfig.data;
    let location,
      layers = [];

    if (isScaled) {
      location = dataSet.find((country) => country.name === geometry);
    } else {
      location = dataSet.find(
        (country) => country.name === "Eastern Caribbean"
      );
    }

    if (stat) {
      layers = location?.stats.find((l) => l.id === stat.short)?.layers;
    } else {
      layers = location?.stats[0].layers;
    }

    if (layers.length) {
      //and set default context layers
      setContextLayers(
        layers.map((l) => (l.isDefault ? l.name : null)).filter((a) => a)
      );
    }

    return layers;
  };

  /**
   * Only one model layer is active at a time. Memoize it and update when user selections change
   */
  const layers = useMemo(() => {
    if (!stats.length) {
      return null;
    }
    const lu = stats.find((s) => s.id === selectedStat);
    const dataLayers = getLayersForSelections(
      lu,
      selectedGeometry,
      isGeoScaled
    );

    const layers = dataLayers.map(
      (l) =>
        new FeatureLayer({
          url: FisheriesConfig.rootURL + l.url,
          id: l.name,
          visible: l.name === selectedStat || (!selectedStat && l.isDefault),
          title: l.title || l.name,
          overrideTitle: l.label,
          definitionExpression:
            l.isShared &&
              isGeoScaled &&
              selectedGeometry !== "Eastern Caribbean"
              ? `Country = '${utils.getCountryForEEZ(selectedGeometry)}'`
              : "1=1",
        })
    );

    return layers;
  }, [stats, selectedStat, selectedGeometry, isGeoScaled]);

  /**
   * When the memoized layer object updates, we need to remove the current layer
   */
  useEffect(() => {
    layers?.forEach((l) => mapObject?.map.remove(l));
  }, [mapObject?.map, layers]);

  /**
   * Second effect declared to add the new layer. Runs after first efect that removes. Has callback for removing on component unmount
   */
  useEffect(() => {
    layers?.forEach((l) => mapObject?.map.add(l));
    return () => {
      layers?.forEach((l) => mapObject?.map.remove(l));
    };
  }, [mapObject?.map, layers]);

  /**
   * On component mount, fetch the stats table and prepare a query tasks to execute queries on
   */

  const queryTable = useMemo(() => {
    return new QueryTask({
      url: FisheriesConfig.rootURL + FisheriesConfig.statsTable,
    });
  }, []);

  /**
   * Fetch the stats for the currrent app and set them in state.
   */
  useEffect(() => {
    const query = new Query();
    query.returnGeometry = false;
    query.outFields = ["*"];
    query.where = "App = 'Coral Reef Fisheries'";

    queryTable.execute(query).then((results) => {
      const newStats = [];
      // console.log(FisheriesConfig);
      results.features.forEach((feature) => {
        const lu = FisheriesConfig.stats.find(
          (f) => f.id === feature.attributes.Statistic
        );

        newStats.push({ ...feature.attributes, ...lu });
      });
      newStats.sort((a, b) => (a.order > b.order ? 1 : -1));
      setStats(newStats);
    });
  }, [queryTable, setStats]);

  useEffect(() => {
    //if a selected stat was provided via the querystring, make sure active setting matches selection
    if (stats.length) {
      const qu = stats.find((f) => f.id === selectedStat);

      if (!qu.active) {
        const updatedStats = [...stats];
        updatedStats
          .filter((s) => s.short === qu.short)
          .forEach((s) => (s.active = s.id === selectedStat));
        setStats(updatedStats);
      }
    }
  }, [stats, selectedStat]);

  /**
  * Simple handler for when the user selects a new geometry. Update in state
  */

  const handleGeometryChange = (geometry) => {
    setSelectedGeometry(geometry);
    //auto-scale data if anything is selected
    setIsGeoScaled(geometry !== "CROP Countries");
  };

  /**
   * Simple handler for when the user toggle the geo scaler. Update in state
   */
  const onGeoScaleToggle = () => {
    setIsGeoScaled(!isGeoScaled);
  };

  /**
   * Simple handler for when the user clicks on a stat. Update in state
   */
  const handleStatClick = (stat) => {
    setSelectedStat(stat.id);

    //clear scale for estiamte cumulative fishing impact
    if (stat.hideScale) {
      setIsGeoScaled(false);
    } else if (selectedGeometry !== "Eastern Caribbean") {
      setIsGeoScaled(true);
    }
  };

  /**
   * Simple handler for when the user clicks on a bar in the chart. Nulls out if user clicks active bar. Update in state
   */
  const handleChartClick = (country) => {
    setSelectedGeometry(country);
    const geo = geometries.find((geo) => geo.name === country);
    mapObject.view.goTo(new Extent(geo.extent));
  };

  /**
   * Toggles the visibility of a reference layer by adding/removing from an array that tracks currently visible ref layers
   */
  const handleRefLayerToggle = (layer) => {
    if (refLayersEnabled.includes(layer)) {
      //turn off
      setRefLayersEnabled([]);
    } else {
      //turn on
      setRefLayersEnabled([layer]);
    }
  };

  /**
   * Toggles the visibility of a context layer by adding/removing from an array that tracks currently visible context layers
   */
  const handleContextLayerToggle = (e) => {
    const name = e.currentTarget.value;
    const layer = layers.find((l) => l.id === name);
    //updated to radio
    layers.filter((l) => l.id !== name).forEach((l) => (l.visible = false));
    //radio means all previous
    if (contextLayers.indexOf(name) === -1) {
      //update map
      layer.visible = true;
      //updated to radio
      setContextLayers([name]);
    }

    //update stats to reflect selected layer
    const newStats = [...stats];
    const foundStat = newStats.find((s) => s.Statistic === name);
    if (foundStat) {
      newStats
        .filter((s) => s.short === foundStat.short)
        .forEach((s) => (s.active = false));
      foundStat.active = true;
      setStats(newStats);
      setSelectedStat(foundStat.id);
    }
  };

  const luStat = stats.find((s) => s.Statistic === selectedStat);

  return (
    <Form className="fisheries">
      <p className="instructions">
        This app demonstrates the impact of human populations on fish stocks and
        supports fisheries management by helping to identify areas that are most
        likely to benefit from improved management. We include data from the
        five CROP countries as well as adjacent island nations included in the
        model to account for connectivity among fish populations.
      </p>
      {!mobileActive && (
        <div className="geoSelect">
          <GeoSelector handleOnChange={handleGeometryChange} />
          {!luStat ||
            (luStat && !luStat.hideScale && (
              <GeoScale
                geoScaled={Boolean(isGeoScaled)}
                onToggle={onGeoScaleToggle}
              />
            ))}
        </div>
      )}
      <div className="quickStats">
        <h3>Quick Stats for {selectedGeometry}</h3>
        <h5>
          These summary statistics represent the MEAN values for each parameter
          summarized by the selected region. Click the statistics below to
          change the map and graph.
        </h5>
        <FisheriesStats
          stats={stats}
          selectedGeometry={selectedGeometry}
          selectedStat={selectedStat}
          handleStatClick={handleStatClick}
          layersToInclude={layers}
          layersEnabled={contextLayers}
          handleLayerToggle={handleContextLayerToggle}
        />
      </div>
      <div className="chart">
        {Object.keys(stats).length > 0 && (
          <FisheriesChart
            stats={stats}
            selectedStat={selectedStat}
            handleChartClick={handleChartClick}
            selectedGeometry={selectedGeometry}
          />
        )}
      </div>
      <div className="otherMaps">
        <h3>Additional Map Layers:</h3>
        <ReferenceLayers
          layersToInclude={FisheriesConfig.refLayers}
          layersEnabled={refLayersEnabled}
          isRadio={true}
          handleLayerToggle={handleRefLayerToggle}
        />
      </div>
      {FisheriesConfig.references && (
        <div className="references-wrapper">
          <h3>References</h3>
          <References refs={FisheriesConfig.references} />
        </div>
      )}
    </Form>
  );
}

export default Fisheries;
